'use client';

import { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

type PeriodType = 'monthly' | 'quarterly' | 'yearly';

interface Period {
  id: string;
  label: string;
  value: string;
  type: PeriodType;
}

interface PeriodSelectorProps {
  className?: string;
  onPeriodChange?: (period: Period) => void;
}

const PeriodSelector = ({ className = '', onPeriodChange }: PeriodSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<Period | null>(null);
  const [periodType, setPeriodType] = useState<PeriodType>('monthly');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const periods: Record<PeriodType, Period[]> = {
    monthly: [
      { id: 'm-2026-02', label: 'February 2026', value: '2026-02', type: 'monthly' },
      { id: 'm-2026-01', label: 'January 2026', value: '2026-01', type: 'monthly' },
      { id: 'm-2025-12', label: 'December 2025', value: '2025-12', type: 'monthly' },
      { id: 'm-2025-11', label: 'November 2025', value: '2025-11', type: 'monthly' },
      { id: 'm-2025-10', label: 'October 2025', value: '2025-10', type: 'monthly' },
    ],
    quarterly: [
      { id: 'q-2026-q1', label: 'Q1 2026', value: '2026-Q1', type: 'quarterly' },
      { id: 'q-2025-q4', label: 'Q4 2025', value: '2025-Q4', type: 'quarterly' },
      { id: 'q-2025-q3', label: 'Q3 2025', value: '2025-Q3', type: 'quarterly' },
      { id: 'q-2025-q2', label: 'Q2 2025', value: '2025-Q2', type: 'quarterly' },
    ],
    yearly: [
      { id: 'y-2026', label: 'FY 2026', value: '2026', type: 'yearly' },
      { id: 'y-2025', label: 'FY 2025', value: '2025', type: 'yearly' },
      { id: 'y-2024', label: 'FY 2024', value: '2024', type: 'yearly' },
    ],
  };

  useEffect(() => {
    const savedPeriod = localStorage.getItem('selected-period');
    if (savedPeriod) {
      const parsed = JSON.parse(savedPeriod);
      setSelectedPeriod(parsed);
      setPeriodType(parsed.type);
    } else {
      const defaultPeriod = periods.monthly[0];
      setSelectedPeriod(defaultPeriod);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handlePeriodSelect = (period: Period) => {
    setSelectedPeriod(period);
    setPeriodType(period.type);
    localStorage.setItem('selected-period', JSON.stringify(period));
    setIsOpen(false);
    onPeriodChange?.(period);
  };

  const handleTypeChange = (type: PeriodType) => {
    setPeriodType(type);
    const firstPeriod = periods[type][0];
    setSelectedPeriod(firstPeriod);
    localStorage.setItem('selected-period', JSON.stringify(firstPeriod));
    onPeriodChange?.(firstPeriod);
  };

  if (!selectedPeriod) {
    return null;
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground border border-border rounded-md hover:bg-muted hover:shadow-md transition-all duration-250 ease-out active:scale-98 min-w-[180px] sm:min-w-[200px]"
        aria-label="Select period"
        aria-expanded={isOpen}
      >
        <Icon name="CalendarIcon" size={20} className="text-text-secondary flex-shrink-0" />
        <span className="font-mono text-sm font-medium flex-1 text-left truncate">
          {selectedPeriod.label}
        </span>
        <Icon
          name={isOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'}
          size={16}
          className="text-text-secondary flex-shrink-0"
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-full sm:w-80 bg-card border border-border rounded-md shadow-xl z-dropdown overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-secondary">
            <div className="flex gap-2">
              {(['monthly', 'quarterly', 'yearly'] as PeriodType[]).map((type) => (
                <button
                  key={type}
                  onClick={() => handleTypeChange(type)}
                  className={`flex-1 px-3 py-2 rounded-md font-mono text-xs font-medium transition-all duration-250 ease-out ${
                    periodType === type
                      ? 'bg-accent text-accent-foreground shadow-md'
                      : 'bg-primary text-text-secondary hover:bg-muted hover:text-foreground'
                  }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {periods[periodType].map((period) => (
              <button
                key={period.id}
                onClick={() => handlePeriodSelect(period)}
                className={`w-full px-4 py-3 text-left font-mono text-sm transition-all duration-250 ease-out ${
                  selectedPeriod.id === period.id
                    ? 'bg-accent text-accent-foreground font-medium'
                    : 'text-foreground hover:bg-secondary'
                }`}
              >
                {period.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PeriodSelector;